import type { Reducer } from '@reduxjs/toolkit';
import {
  combineReducers,
  configureStore,
  createAction,
} from '@reduxjs/toolkit';

import agentSettingsReducer, {
  multilingualAgentSettingsSliceReducers,
} from '@polyai/stream/store/reducers/agent-settings';
import apiIntegrationsReducer from '@polyai/stream/store/reducers/api-integrations';
import asrReducer from '@polyai/stream/store/reducers/asr';
import asrSettingsReducer from '@polyai/stream/store/reducers/asr-settings';
import channelsReducer from '@polyai/stream/store/reducers/channels';
import contentFilterSettingsReducer from '@polyai/stream/store/reducers/content-filter-settings';
import csatReducer from '@polyai/stream/store/reducers/csat';
import {
  applyOverwriteState,
  overwriteState,
} from '@polyai/stream/store/reducers/draft';
import entitiesReducer from '@polyai/stream/store/reducers/entities';
import experimentalConfigReducer from '@polyai/stream/store/reducers/experimental-config';
import flowsReducer from '@polyai/stream/store/reducers/flows';
import functionsReducer from '@polyai/stream/store/reducers/functions';
import handoffReducer from '@polyai/stream/store/reducers/handoff';
import integrationsReducer from '@polyai/stream/store/reducers/integrations';
import keyphraseBoostingReducer from '@polyai/stream/store/reducers/keyphrase-boosting';
import knowledgeBaseReducer from '@polyai/stream/store/reducers/knowledge-base';
import languagesReducer from '@polyai/stream/store/reducers/languages';
import llmSettingsReducer from '@polyai/stream/store/reducers/llm-settings';
import mcpConfigReducer from '@polyai/stream/store/reducers/mcp-config';
import pronunciationsReducer from '@polyai/stream/store/reducers/pronunciations';
import smsReducer from '@polyai/stream/store/reducers/sms';
import specialFunctionsReducer from '@polyai/stream/store/reducers/specialFunctions';
import stopKeywordsReducer from '@polyai/stream/store/reducers/stop-keywords';
import transcriptCorrectionsReducer from '@polyai/stream/store/reducers/transcript-corrections';
import translationsReducer from '@polyai/stream/store/reducers/translations';
import variablesReducer from '@polyai/stream/store/reducers/variables';
import variantReducer from '@polyai/stream/store/reducers/variant';
import voicesReducer from '@polyai/stream/store/reducers/voices';

export const sliceReducers = {
  asr: asrReducer,
  knowledgeBase: knowledgeBaseReducer,
  sms: smsReducer,
  handoff: handoffReducer,
  flows: flowsReducer,
  variantManagement: variantReducer,
  stopKeywords: stopKeywordsReducer,
  llmSettings: llmSettingsReducer,
  asrSettings: asrSettingsReducer,
  languages: languagesReducer,
  contentFilterSettings: contentFilterSettingsReducer,
  functions: functionsReducer,
  transcriptCorrections: transcriptCorrectionsReducer,
  // Deprecated, refer to multilingualAgentSettings instead
  agentSettings: agentSettingsReducer,
  multilingualAgentSettings: multilingualAgentSettingsSliceReducers,
  experimentalConfig: experimentalConfigReducer,
  pronunciations: pronunciationsReducer,
  keyphraseBoosting: keyphraseBoostingReducer,
  entities: entitiesReducer,
  voices: voicesReducer,
  specialFunctions: specialFunctionsReducer,
  apiIntegrations: apiIntegrationsReducer,
  variables: variablesReducer,
  translations: translationsReducer,
  channels: channelsReducer,
  csat: csatReducer,
  integrations: integrationsReducer,
  mcpConfig: mcpConfigReducer,
};

export const PROJECTION_SLICES = Object.keys(
  sliceReducers,
) as (keyof typeof sliceReducers)[];

export type ProjectionSlice = keyof typeof sliceReducers;

export const rootReducer = combineReducers(sliceReducers);
// combine the slice-by-slice reducers with the overwriteState reducer
export const reducer: Reducer<ReturnType<typeof rootReducer>> = (
  state,
  action,
) => {
  const updated = rootReducer(state, action);

  if (overwriteState.match(action)) {
    return applyOverwriteState(updated, action);
  }

  return updated;
};

const store = configureStore({
  reducer: reducer,
});

export type RootState = ReturnType<typeof store.getState>;

export type AppStore = typeof store;
export type AppDispatch = ReturnType<typeof store.dispatch>;
export type ActionMeta = {
  createdAt: string;
  createdBy: string;
};

export const EMPTY_ACTION = createAction('EMPTY');
